import { useEffect, useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Lightning, Copy, Key } from "@phosphor-icons/react";
import { CardShell, fmtWhen } from "./shared";

export default function ZapierCard({ item, isAdmin }) {
  const extra = item?.extra || {};
  const [zaps, setZaps] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    API.get("/integrations/zapier/zaps").then(({ data }) => setZaps(data.items || [])).catch(() => setZaps([]));
  }, [open]);

  const copy = async (text, label) => { try { await navigator.clipboard.writeText(text); } catch { window.prompt("Copy:", text); } toast.success(`${label} copied`); };
  const status = extra.active_zaps > 0 ? "connected" : null;

  return (
    <CardShell icon={<Lightning size={18} weight="duotone" className="text-orange-500" />} title="Zapier" item={{ ...item, connection: status ? { status } : null }} testid="integration-zapier">
      <div className="space-y-3 text-xs">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" data-testid="zapier-active-zaps">{extra.active_zaps || 0} active Zap trigger{extra.active_zaps === 1 ? "" : "s"}</Badge>
          <Badge variant="outline" data-testid="zapier-api-keys">{extra.api_keys || 0} API key{extra.api_keys === 1 ? "" : "s"}</Badge>
          {extra.status === "public" && <Badge>Public app</Badge>}
        </div>
        <ol className="list-decimal pl-4 space-y-1 text-muted-foreground">
          <li>Create an API key under <a href="/settings?tab=apikeys" className="text-primary underline" data-testid="zapier-apikeys-link"><Key size={11} className="inline" /> Settings → API Keys</a> — Zapier asks for it when you connect Fixlr.</li>
          <li>
            {extra.invite_url
              ? <>Accept the Fixlr app invite: <a href={extra.invite_url} target="_blank" rel="noreferrer" className="text-primary underline break-all" data-testid="zapier-invite-link">{extra.invite_url}</a></>
              : <span data-testid="zapier-invite-missing">The Fixlr Zapier app hasn't been published yet — the platform team will add the invite link here.</span>}
          </li>
          <li>Build a Zap: pick a Fixlr trigger, connect the app you want, done.</li>
        </ol>
        <div>
          <p className="font-medium mb-1">Triggers available</p>
          <div className="flex flex-wrap gap-1" data-testid="zapier-triggers">
            {Object.entries(extra.triggers || {}).map(([k, v]) => <Badge key={k} variant="secondary" className="text-[10px]">{v}</Badge>)}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Actions: Create Request, Create Job, Create Client, Find Client, Find Job.</p>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="text-muted-foreground">API base for developers:</span><code className="break-all" data-testid="zapier-api-base">{extra.api_base}</code>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copy(extra.api_base, "API base")} data-testid="copy-zapier-api-base"><Copy size={12} /></Button>
        </div>
        {isAdmin && (
          <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} data-testid="zapier-zaps-toggle">{open ? "Hide connected Zaps" : "Show connected Zaps"}</Button>
        )}
        {open && (
          <div className="rounded-md border border-border divide-y" data-testid="zapier-zaps">
            {zaps === null ? <p className="p-2 text-muted-foreground">Loading…</p> : zaps.length === 0 ? <p className="p-2 text-muted-foreground" data-testid="zapier-zaps-empty">No Zaps subscribed yet.</p> : zaps.map((z) => (
              <div key={z.id} className="p-2 flex items-center justify-between gap-2" data-testid={`zapier-zap-${z.id}`}>
                <div><b>{(extra.triggers || {})[z.event] || z.event}</b><span className="text-muted-foreground"> · since {fmtWhen(z.created_at)}</span>{z.last_error && <span className="text-red-600"> · {z.last_error}</span>}</div>
                <Badge variant={z.active ? "default" : "secondary"}>{z.active ? "active" : "paused"}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </CardShell>
  );
}
